// Looks colour maths (spec 2026-10-01 looks §1.2.3, §4.4, §4.5), shared with
// the app (Workspace369 lib/common/looks/look_color.dart and
// lib/view/shared/appearance_studio/accent_contrast.dart). Pure, no imports.
//
// `mix` is the canvas's linear sRGB interpolation, rounded per channel.
// Contrast is WCAG 2.x with the 0.03928 linearisation knee that
// utils/portal/branding.ts accentInk() and the app's contrastRatio() use, so
// a ratio here is the ratio there. Pinned by the shared vectors
// (utils/looks/__fixtures__/looks-resolution-vectors.v1.json `ink_on`, `mix`).

/** `#RRGGBB`, any case. The only colour form a look stores or emits. */
export const LOOK_HEX = /^#[0-9A-Fa-f]{6}$/

export const LOOK_INK_LIGHT = '#FFFFFF'
export const LOOK_INK_DARK = '#141414'

export function isLookHex(value: unknown): value is string {
  return typeof value === 'string' && LOOK_HEX.test(value)
}

function channels(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('').toUpperCase()}`
}

/** [a] moved toward [b] by [t] (0–1), per sRGB channel: the canvas's `mix`. Upper-case `#RRGGBB`. */
export function mix(a: string, b: string, t: number): string {
  const A = channels(a)
  const B = channels(b)
  return toHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t)
}

function linear(channel: number): number {
  const s = channel / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex)
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

/** WCAG 2 contrast ratio, 1–21, unrounded. */
export function contrast(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** A page is dark when its relative luminance is under 0.18 (the canvas's `isDark`). */
export function isDarkColor(hex: string): boolean {
  return relativeLuminance(hex) < 0.18
}

/**
 * The look-era button text on [hex]: white when white reaches 3:1, else
 * `#141414` (spec §4.4, the canvas's `autoText`). Pages with no look keep
 * round 1's accentInk (utils/portal/branding.ts), which is G0.
 */
export function lookInkOn(hex: string): string {
  return contrast(LOOK_INK_LIGHT, hex) >= 3 ? LOOK_INK_LIGHT : LOOK_INK_DARK
}

/** A ratio for display, TRUNCATED to one decimal (4.49 → "4.4"), so a failing value never reads as passing. */
export function displayRatio(ratio: number): string {
  return (Math.floor(ratio * 10) / 10).toFixed(1)
}

export function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = channels(hex).map(v => v / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h *= 60
  }
  return [h, s * 100, l * 100]
}

export function hslToHex(h: number, s: number, l: number): string {
  const sat = s / 100
  const lig = l / 100
  const k = (n: number) => (n + h / 30) % 12
  const a = sat * Math.min(lig, 1 - lig)
  const f = (n: number) => lig - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  return toHex(f(0) * 255, f(8) * 255, f(4) * 255)
}

export interface BrandReadabilityInput {
  brand: string
  /** The button text on [brand]. */
  on: string
  /** The link colour (the brand when it overrides the palette). */
  link: string
  page: string
  card: string
}

export interface BrandReadability {
  /** contrast(on, brand); passes at 4.5. */
  button: number
  /** min(contrast(link, page), contrast(link, card)); passes at 3. */
  links: number
  ok: boolean
}

/** The one readability rule (spec §4.4). Compares unrounded ratios. */
export function checkBrandReadability(input: BrandReadabilityInput): BrandReadability {
  const button = contrast(input.on, input.brand)
  const links = Math.min(contrast(input.link, input.page), contrast(input.link, input.card))
  return { button, links, ok: button >= 4.5 && links >= 3 }
}

export interface ShadeFix {
  hex: string
  direction: 'darker' | 'lighter'
}

/**
 * The shade fix (spec §4.5): step the brand colour's HSL lightness by 1 —
 * first darker when the button fails under white text (lighter under
 * `#141414`), or, when only links fail, darker on a light page and lighter on
 * a dark one — until both checks pass with [on] kept fixed and the candidate
 * as the link; then the other way. Null when nothing passes.
 */
export function fixShade(brand: string, on: string, page: string, card: string): ShadeFix | null {
  const start = checkBrandReadability({ brand, on, link: brand, page, card })
  if (start.ok) return null
  const first = start.button < 4.5
    ? (on.toUpperCase() === LOOK_INK_LIGHT ? -1 : 1)
    : (isDarkColor(page) ? 1 : -1)
  const [h, s, l] = hexToHsl(brand)
  for (const dir of [first, -first]) {
    for (let i = 1; i <= 100; i++) {
      const lightness = l + dir * i
      if (lightness < 0 || lightness > 100) break
      const candidate = hslToHex(h, s, lightness)
      if (checkBrandReadability({ brand: candidate, on, link: candidate, page, card }).ok) {
        return { hex: candidate, direction: dir < 0 ? 'darker' : 'lighter' }
      }
    }
  }
  return null
}
